import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../Common/Header.jsx';
import HowToPlay from './HowToPlay.jsx';
import Lobby from './Lobby.jsx';
import WaveformVisualizer from '../Common/WaveformVisualizer.jsx';
import PhaseStatus from '../Common/PhaseStatus.jsx';
import VersionTag from '../Common/VersionTag.jsx';
import { SoundOffHost } from '../modes/SoundOff/SoundOffHost.jsx';
import DriftHost from '../modes/Drift/DriftHost.jsx';
import { NameThatNoiseHost } from '../modes/NameThatNoise/NameThatNoiseHost.jsx';
import { FoleyHost } from '../modes/Foley/FoleyHost.jsx';
import useAudioEngine from '../../audio/useAudioEngine.js';
import useClipLookup from '../Common/useClipLookup.js';
import io from '../../utils/socketWrapper.js';
import '../../styles/theme.css';

// A stable host id per room, so a reload or a dropped socket reclaims the same
// host seat. The server now refuses a create_room from a different id once a
// room has a host — without a stable id here, the host's own reconnect would
// be the thing that got locked out.
// Mirrors the modes' own minPlayers so the rules screen can grey out Start.
// The server enforces it for real; this is only an affordance.
const MIN_PLAYERS = { soundoff: 3, drift: 3, foley: 3, namethatnoise: 2 };

function stableHostId(roomCode) {
  const key = `sounddrift:hid:${roomCode}`;
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const id = 'host_' + Math.random().toString(36).substring(2, 11);
    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return 'host_' + Math.random().toString(36).substring(2, 11);
  }
}

export function HostShell({ children, mockRoom = null }) {
  const [searchParams] = useSearchParams();
  const paramRoomCode = searchParams.get('roomCode');
  const [socket, setSocket] = useState(null);
  const [room, setRoom] = useState(mockRoom);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [selectedMode, setSelectedMode] = useState('soundoff');
  // The rules screen is opt-in and always skippable: it never sits between the
  // host and starting a round.
  const [showRules, setShowRules] = useState(false);
  const [gameError, setGameError] = useState(null);
  const [progress, setProgress] = useState(null);

  // The host screen is the only speaker (REQUIREMENTS §2), so it runs the
  // engine at full gain.
  const audio = useAudioEngine('host');
  const knownPlayersRef = useRef(new Set());
  const lookupClip = useClipLookup();
  const lookupClipRef = useRef(null);
  const playOnHostRef = useRef(null);
  const soundEnabledRef = useRef(false);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [playProgress, setPlayProgress] = useState(0);

  // Room code allocation. Without an explicit ?roomCode, ask the server for a
  // random one. This used to fall back to the literal string 'HOST', so every
  // host that opened /host landed in the SAME Durable Object and shared a room
  // with strangers.
  const [roomCode, setRoomCode] = useState(paramRoomCode || null);

  useEffect(() => {
    if (mockRoom || paramRoomCode) return;
    let cancelled = false;

    fetch('/api/room/new')
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled && d?.roomCode) {
          setRoomCode(d.roomCode);
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem('sounddrift_roomCode', d.roomCode);
          }
          if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
            window.history.replaceState({}, '', `/host?roomCode=${d.roomCode}`);
          }
        }
      })
      .catch(() => {
        if (!cancelled) setGameError('Could not allocate a room code. Please reload.');
      });

    return () => { cancelled = true; };
  }, [paramRoomCode, mockRoom]);

  useEffect(() => {
    if (mockRoom || !roomCode) return;

    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('sounddrift_roomCode', roomCode);
    }

    const s = io(`${window.location.origin}?roomCode=${roomCode}`, { query: { roomCode }, reconnection: true });
    setSocket(s);

    s.on('connect', () => {
      s.emit('create_room', {
        roomCode,
        playerId: stableHostId(roomCode),
        playerName: 'Host (TV)',
        avatar: '📺',
      });
    });

    s.on('room_created', (data) => setRoom(data.room));
    s.on('room_update', (data) => {
      setRoom(data.room);
      if (data?.progress) setProgress(data.progress);

      // A player's avatar IS their signature sound, so announce arrivals with
      // it. This is the payoff of picking a sound instead of an emoji.
      const players = Object.values(data?.room?.players || {});
      for (const p of players) {
        if (p.isHost || knownPlayersRef.current.has(p.id)) continue;
        knownPlayersRef.current.add(p.id);
        if (p.avatar && soundEnabledRef.current) {
          const c = lookupClipRef.current?.(p.avatar);
          if (c) playOnHostRef.current?.(c); else audio.play(p.avatar);
        }
      }
    });
    s.on('game_started', (data) => setRoom(data.room));
    s.on('phase_change', (data) => {
      if (data?.room) setRoom(data.room);
      if (data?.progress) setProgress(data.progress);
    });

    // Surface server-side refusals. The bug this replaces was silent: the
    // server declined to start and the host screen simply sat in the lobby.
    s.on('game_error', (data) => {
      setGameError(data?.message || 'The game could not be started.');
    });

    return () => {
      s.disconnect();
    };
  }, [roomCode, mockRoom]);

  const handleEnableSound = async () => {
    // Must run inside the user gesture: browsers only resume an AudioContext
    // from a real interaction.
    await audio.unlock();
    soundEnabledRef.current = true;
    setSoundEnabled(true);
    if (typeof window !== 'undefined') {
      window.__audioProbe = window.__audioProbe || {};
      window.__audioProbe.unlocked = true;
    }
  };

  useEffect(() => {
    const handleMessage = (e) => {
      if (e.data && (e.data.type === 'UNLOCK_AUDIO' || e.data.type === 'ENABLE_SOUND')) {
        handleEnableSound();
      }
    };
    const handleWindowClick = () => {
      if (!soundEnabledRef.current) {
        handleEnableSound();
      }
    };
    window.addEventListener('message', handleMessage);
    window.addEventListener('click', handleWindowClick, { capture: true });
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('click', handleWindowClick, { capture: true });
    };
  }, []);

  // Tell the server which mode is selected. The lobby previously routed this
  // through update_settings, which stores into room.settings and never touches
  // room.currentMode — so the selection never reached the engine.
  const handleSelectMode = (modeId) => {
    setSelectedMode(modeId);
    setGameError(null);
    if (socket) {
      socket.emit('select_mode', { roomCode, mode: modeId });
    }
  };

  const handleStartGame = () => {
    if (!socket) return;
    setGameError(null);
    // Carry the mode on start_game too, so a dropped select_mode cannot start
    // the wrong game.
    socket.emit('start_game', { roomCode, mode: selectedMode });
  };

  const handleUpdateSettings = (settings) => {
    if (!socket) return;
    if (settings?.mode && settings.mode !== selectedMode) {
      handleSelectMode(settings.mode);
    }
    socket.emit('update_settings', { roomCode, settings });
  };

  const handleKickPlayer = (playerId) => {
    if (socket) {
      socket.emit('kick_player', { roomCode, targetPlayerId: playerId });
    }
  };

  const handleForceAdvance = () => {
    if (socket) socket.emit('force_advance', { roomCode });
  };

  const handleReturnToLobby = () => {
    if (socket) {
      socket.emit('reset_to_lobby', { roomCode });
    }
  };

  const handleRestartNewRoom = async () => {
    try {
      if (socket) {
        socket.disconnect();
      }
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('sounddrift_roomCode');
        if (roomCode) sessionStorage.removeItem(`sounddrift:hid:${roomCode}`);
      }
      const res = await fetch('/api/room/new');
      const d = await res.json();
      if (d?.roomCode) {
        setRoomCode(d.roomCode);
        setRoom(null);
        setGameError(null);
        setProgress(null);
        setNowPlaying(null);
        knownPlayersRef.current.clear();
        if (typeof window !== 'undefined' && window.history && window.history.pushState) {
          window.history.pushState({}, '', `/host?roomCode=${d.roomCode}`);
        }
      }
    } catch {
      setGameError('Could not allocate a new room. Please reload.');
    }
  };

  const handleSetMysteryName = (customName) => {
    if (socket) {
      socket.emit('set_mystery_name', { roomCode, label: customName });
    }
  };

  // Play a clip on the TV and drive the waveform playhead from its real
  // duration, so the sweep matches the audio rather than a fixed 3s guess.
  const playOnHost = async (clip) => {
    if (!clip) return;
    setNowPlaying(clip);
    setPlayProgress(0);
    // The playhead is driven by the audio clock, so the cursor sits exactly
    // where the sound is. It used to run off Date.now() started before the
    // buffer did, which drifted and kept moving after the clip had finished.
    await audio.play(clip, {
      onProgress: (d) => setPlayProgress(Math.min(1, Math.max(0, d?.progress ?? 0))),
      onEnded: () => setPlayProgress(1),
    });
  };

  lookupClipRef.current = lookupClip;
  playOnHostRef.current = playOnHost;

  const displayRoomCode = room?.roomCode || roomCode || '';
  const playersMap = room?.players || {};
  const playersList = Object.values(playersMap).filter((p) => !p.isHost);

  // The server writes `currentPhase`; this read `room.phase`, which never
  // exists, so the host screen stayed in the lobby for every mode.
  const phase = room?.currentPhase || 'lobby';
  const currentMode = room?.currentMode || selectedMode;

  const renderActiveMode = () => {
    if (children) return children;

    switch (currentMode) {
      case 'soundoff':
        return <SoundOffHost room={room} audioEngine={audio} onForceAdvance={handleForceAdvance} />;
      case 'drift':
        return <DriftHost room={room} audioEngine={audio} onForceAdvance={handleForceAdvance} />;
      case 'namethatnoise':
        return (
          <NameThatNoiseHost
            room={room}
            audioEngine={audio}
            phase={phase}
            modeState={room?.modeState || {}}
            players={playersMap}
            onPlayAudio={(clip) => playOnHost(clip || room?.modeState?.mysteryClip)}
            onSetMysteryName={handleSetMysteryName}
            playbackProgress={playProgress}
            onForceAdvance={handleForceAdvance}
          />
        );
      case 'foley':
        return (
          <FoleyHost
            room={room}
            audioEngine={audio}
            soundEnabled={soundEnabled}
            onEnableSound={handleEnableSound}
            onForceAdvance={handleForceAdvance}
          />
        );
      default:
        return (
          <div data-testid="unknown-mode-error" style={{ padding: '24px', color: '#f87171' }}>
            Unknown game mode "{currentMode}".
          </div>
        );
    }
  };

  return (
    <div className="host-tv-shell" data-testid="host-shell">
      <div className="host-tv-content">
        <Header
          isHost={true}
          roomCode={displayRoomCode}
          playerCount={playersList.length}
          soundEnabled={soundEnabled}
          onEnableSound={handleEnableSound}
          onForceAdvance={handleForceAdvance}
          onRestartRoom={handleRestartNewRoom}
          onReturnToLobby={handleReturnToLobby}
          currentPhase={phase}
        />

        {gameError && (
          <div
            data-testid="game-error"
            role="alert"
            style={{
              background: '#7f1d1d', color: '#fecaca', padding: '12px 20px',
              borderRadius: '10px', margin: '12px 0', fontWeight: 700,
            }}
          >
            ⚠️ {gameError}
          </div>
        )}

        {phase === 'lobby' && showRules ? (
          <HowToPlay
            mode={selectedMode}
            playerCount={playersList.length}
            minPlayers={MIN_PLAYERS[selectedMode] || 0}
            onSkip={() => setShowRules(false)}
            onBack={() => setShowRules(false)}
            onStart={() => { setShowRules(false); handleStartGame(); }}
          />
        ) : phase === 'lobby' ? (
          <Lobby
            roomCode={displayRoomCode}
            players={playersList}
            soundEnabled={soundEnabled}
            onEnableSound={handleEnableSound}
            onStartGame={handleStartGame}
            onSelectMode={handleSelectMode}
            onUpdateSettings={handleUpdateSettings}
            onKickPlayer={handleKickPlayer}
            onPlayClip={playOnHost}
            onShowRules={() => setShowRules(true)}
            onRestartRoom={handleRestartNewRoom}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <PhaseStatus
              phase={phase}
              progress={progress}
              phaseEndTime={room?.phaseEndTime}
              isHost={true}
              onForceAdvance={handleForceAdvance}
            />
            {/* Modes that hide the clip's identity render their own masked
                visualiser. Showing a second one here printed the mystery
                clip's real label on the TV during the bluffing phase — it gave
                away the answer players are supposed to guess. */}
            {currentMode !== 'namethatnoise' && (
              <WaveformVisualizer
                clip={nowPlaying}
                isPlaying={playProgress > 0 && playProgress < 1}
                progress={playProgress}
                duration={nowPlaying?.duration || 3.0}
                clipTitle={nowPlaying?.label || 'Nothing playing'}
              />
            )}
            {renderActiveMode()}
          </div>
        )}
      </div>
      <VersionTag />
    </div>
  );
}

export default HostShell;
